// components/CompetitorSpy.jsx - Updated with Processing Status Tracking
import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Search, Eye, Loader2, AlertCircle, CheckCircle, Crown, Zap } from 'lucide-react';
import CompCard from './CompCard';
import ProcessingPopup from './ProcessingPopup';
import {
  fetchAllComp,
  findComp,
  updateFormData,
  resetFormData,
  clearError,
  clearSuccess,
  clearRouteError,
  updateBrandStatus,
  startCompPollingWithCleanup,
  stopCompPollingWithCleanup,
  selectCompProcessingBrands
} from '../../store/slices/competitorSlice';

// Comp Limit Exceeded Component
const CompLimitExceeded = ({ limitData, onUpgrade, onClose }) => {
  const getPlanColor = (plan) => {
    const colors = {
      free: 'text-gray-600 dark:text-gray-400',
      growth: 'text-green-600 dark:text-green-400',
      pro: 'text-blue-600 dark:text-blue-400',
      enterprise: 'text-purple-600 dark:text-purple-400'
    };
    return colors[plan] || 'text-gray-600 dark:text-gray-400';
  };

  const getPlanIcon = (plan) => {
    if (plan === 'enterprise') return <Crown className="w-5 h-5" />;
    return <Zap className="w-5 h-5" />;
  };

  return (
    <div className="mb-6 p-6 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-700 rounded-xl shadow-sm">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <div className="w-12 h-12 bg-amber-100 dark:bg-amber-800 rounded-full flex items-center justify-center">
            <Crown className="w-6 h-6 text-amber-600 dark:text-amber-400" />
          </div>
        </div>

        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg font-semibold text-amber-900 dark:text-amber-100">
              Competitor Analysis Limit Reached
            </h3>
          </div>

          <p className="text-amber-800 dark:text-amber-200 mb-4">
            You've analyzed <strong>{limitData.currentCompCount}</strong> out of <strong>{limitData.allowedComps}</strong> competitors
            allowed on your <span className={`font-semibold capitalize ${getPlanColor(limitData.userPlan)}`}>
              {limitData.userPlan}
            </span> plan.
          </p>



          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={onUpgrade}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white font-medium rounded-lg transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
            >
              <Crown className="w-4 h-4" />
              <span>View Pricing Plans</span>
            </button>

            <button
              onClick={onClose}
              className="inline-flex items-center gap-2 px-6 py-3 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 text-amber-800 dark:text-amber-200 font-medium rounded-lg border border-amber-300 dark:border-amber-600 transition-all duration-200"
            >
              <span>Maybe Later</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const CompetitorSpy = () => {
  const dispatch = useDispatch();
  const {
    brands,
    isLoadingBrands,
    isAnalyzing,
    error,
    routeError,
    successMessage,
    formData,
    processingBrands
  } = useSelector((state) => state.competitor);

  const [formErrors, setFormErrors] = useState({});
  const [compLimitData, setCompLimitData] = useState(null);
  const [showLimitExceeded, setShowLimitExceeded] = useState(false);
  const [showProcessingPopup, setShowProcessingPopup] = useState(false);
  const [processingBrandInfo, setProcessingBrandInfo] = useState({ brandName: '', brandUrl: '' });
  const [urlVerified, setUrlVerified] = useState(false);
  const [verifyingUrl, setVerifyingUrl] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');

  const API_BASE = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';
  const verifyTimerRef = useRef(null);

  // Fetch brands ONLY once on component mount
  useEffect(() => {
    dispatch(fetchAllComp());
  }, [dispatch]);

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        dispatch(clearSuccess());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, dispatch]);

  // Clear route error after 7 seconds
  useEffect(() => {
    if (routeError) {
      const timer = setTimeout(() => {
        dispatch(clearRouteError());
      }, 7000);
      return () => clearTimeout(timer);
    }
  }, [routeError, dispatch]);

  // Cleanup polling intervals on unmount
  useEffect(() => {
    return () => {
      // Stop all active polling when component unmounts
      Object.keys(processingBrands).forEach(brandUrl => {
        if (processingBrands[brandUrl]?.isActive) {
          dispatch(stopCompPollingWithCleanup(brandUrl));
        }
      });
    };
  }, [dispatch, processingBrands]);

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    if (!formData.brandName.trim()) {
      newErrors.brandName = 'Brand name is required';
      isValid = false;
    } else if (/^https?:\/\//i.test(formData.brandName.trim()) || /^www\./i.test(formData.brandName.trim()) || /\.[a-z]{2,}(\/|$)/i.test(formData.brandName.trim())) {
      newErrors.brandName = 'Please enter a brand name, not a URL. Use the Website URL field for the website address.';
      isValid = false;
    }

    if (!formData.spyUrl.trim()) {
      newErrors.spyUrl = 'Website URL is required';
      isValid = false;
    } else {
      // Basic URL validation
      const urlPattern = /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}$/;
      const cleanUrl = formData.spyUrl.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
      
      if (!urlPattern.test(cleanUrl)) {
        newErrors.spyUrl = 'Please enter a valid domain (e.g., example.com)';
        isValid = false;
      }
    }

    setFormErrors(newErrors);
    return isValid;
  };

  const handleInputChange = (field, value) => {
    // Block HTML/script injection
    if (/<[^>]*>/g.test(value)) return;

    // Brand name: only letters, numbers, spaces, dots, hyphens, ampersands
    if (field === 'brandName' && value && !/^[a-zA-Z0-9\s.\-&]*$/.test(value)) return;

    // URL fields: block spaces and invalid chars
    if (field === 'spyUrl' && value && /[\s<>"'{}|\\^`\[\]]/.test(value)) return;

    dispatch(updateFormData({ [field]: value }));

    // Reset URL verification when URL changes
    if (field === 'spyUrl') {
      setUrlVerified(false);
      setVerificationMessage('');
    }

    // Clear field error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    if (error) dispatch(clearError());
    if (routeError) dispatch(clearRouteError());

    if (showLimitExceeded) {
      setShowLimitExceeded(false);
      setCompLimitData(null);
    }
  };

  // Auto-verify URL when user stops typing a valid domain
  useEffect(() => {
    const trimmedUrl = formData.spyUrl?.trim() || '';

    if (!trimmedUrl) {
      setUrlVerified(false);
      setVerificationMessage('');
      return;
    }

    const domainPattern = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}$/;
    const cleanUrl = trimmedUrl.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
    if (!domainPattern.test(cleanUrl)) return;

    if (verifyTimerRef.current) clearTimeout(verifyTimerRef.current);

    verifyTimerRef.current = setTimeout(async () => {
      setVerifyingUrl(true);
      setVerificationMessage('');

      const normalizedUrl = trimmedUrl.startsWith('http') ? trimmedUrl : 'https://' + trimmedUrl;

      try {
        const response = await fetch(`${API_BASE}/api/brandAddition/verify-url`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: normalizedUrl })
        });

        const data = await response.json();

        if (data.success && data.isGamblingBrand) {
          setUrlVerified(true);
          setVerificationMessage(`Verified! Detected: ${data.verticals?.join(', ') || 'Gambling Brand'}`);
        } else {
          setUrlVerified(false);
          setVerificationMessage(data.message || 'This URL does not appear to be a gambling/betting website.');
        }
      } catch (err) {
        setUrlVerified(false);
        setVerificationMessage('Failed to verify URL. Please try again.');
      } finally {
        setVerifyingUrl(false);
      }
    }, 800);

    return () => {
      if (verifyTimerRef.current) clearTimeout(verifyTimerRef.current);
    };
  }, [formData.spyUrl, API_BASE]);

  const handleStartAnalysis = async () => {
    if (!validateForm()) {
      return;
    }

    if (!urlVerified) {
      setFormErrors(prev => ({ ...prev, spyUrl: 'Please verify the URL before starting analysis' }));
      return;
    }

    // Call the findComp function with form data
    const brandName = formData.brandName.trim();
    const spyUrl = formData.spyUrl.trim();

    const competitorData = {
      brandName,
      spyUrl
    };

    try {
      const result = await dispatch(findComp(competitorData));

      // Check if the action was rejected due to comp limit
      if (result.type === 'competitor/findComp/rejected') {
        const errorPayload = result.payload;

        // Check if it's a comp limit error
        if (errorPayload?.error === 'COMP_LIMIT_EXCEEDED' && errorPayload?.data) {
          setCompLimitData(errorPayload.data);
          setShowLimitExceeded(true);
          return;
        }
      }

      // If successful, start polling for processing status
      if (result.type === 'competitor/findComp/fulfilled') {
        const brandId = result.payload?.data?.brandId || result.payload?.brandId;
        const brandUrl = result.payload?.data?.brandUrl || result.payload?.brandUrl || spyUrl;


        if (brandId && brandUrl) {

          // Show processing popup with 30-second countdown
          setProcessingBrandInfo({ brandName, brandUrl, brandId });
          setShowProcessingPopup(true);

          // Start polling for this brand's processing status
          dispatch(startCompPollingWithCleanup({
            brandUrl,
            brandName,
            brandId
          }));
        } else {
          console.error('❌ Cannot start polling - missing brandId or brandUrl:', { brandId, brandUrl });
        }

        // Refresh brands list to show the new brand
        dispatch(fetchAllComp());
      }

    } catch (error) {
      console.error('Analysis error:', error);
    }
  };

  const handleUpgrade = () => {
    // Redirect to pricing/upgrade page
    window.open('/pricing-plan', '_blank');
    // Or navigate using your router: navigate('/pricing-plan');
  };

  const handleCloseLimitMessage = () => {
    setShowLimitExceeded(false);
    setCompLimitData(null);
  };

  const handleCloseProcessingPopup = () => {
    setShowProcessingPopup(false);
  };

  // Handle brand status updates from CompCard - NO UNNECESSARY REFRESHES
  const handleBrandStatusUpdate = (brandId, status) => {
    // Just update the brand status in Redux state - no API call needed
    dispatch(updateBrandStatus({ brandId, status }));
    
    // Only refresh all brands once when processing completes (after 30 seconds)
    // This is to get the final processed data with all metrics
    if (status === 'completed') {
      setTimeout(() => {
        dispatch(fetchAllComp());
      }, 30000); // Wait 30 seconds before refreshing to get final processed data
    }
  };

  // Check if there are any active processing brands
  const hasActiveProcessing = Object.values(processingBrands).some(brand => brand.isActive);

  return (
    <div className="p-4 min-h-screen bg-gray-100 dark:bg-slate-950">
      <div className="mb-6">
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-6">
          <h1 className="text-2xl font-semibold mb-2 text-gray-800 dark:text-slate-100 flex items-center gap-3">
            <Eye className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            Competitor Spy
          </h1>
          <p className="text-gray-600 dark:text-slate-400">
            Uncover your competitors' strategies, keywords, and traffic sources
          </p>
        </div>
      </div>

      {/* Comp Limit Exceeded Message */}
      {showLimitExceeded && compLimitData && (
        <CompLimitExceeded 
          limitData={compLimitData}
          onUpgrade={handleUpgrade}
          onClose={handleCloseLimitMessage}
        />
      )}

      <div className="mb-6">
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-800 dark:text-slate-100">Competitor Analysis Setup</h2>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 dark:bg-green-400 rounded-full"></div>
                <p className="text-green-700 dark:text-green-300 font-medium">{successMessage}</p>
              </div>
              <p className="text-green-600 dark:text-green-400 text-sm mt-1">
                The analysis will appear below and will be processed automatically.
              </p>
            </div>
          )}

          {/* General Error Message */}
          {error && !showLimitExceeded && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400" />
                <p className="text-red-700 dark:text-red-300 font-medium">{error}</p>
              </div>
            </div>
          )}

          {/* Route Error Message - Warning (yellow) for non-critical issues like duplicates */}
          {routeError && (
            <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-yellow-800 dark:text-yellow-200 font-medium text-sm">{routeError}</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                Competitor Brand Name *
              </label>
              <input
                type="text"
                value={formData.brandName}
                onChange={(e) => {
                  const val = e.target.value;
                  handleInputChange('brandName', val);
                  if (/^https?:\/\//i.test(val.trim()) || /^www\./i.test(val.trim()) || /\.[a-z]{2,}(\/|$)/i.test(val.trim())) {
                    setFormErrors(prev => ({ ...prev, brandName: 'Please enter a brand name, not a URL. Use the Website URL field for the website address.' }));
                  }
                }}
                placeholder="e.g., BetWin Casino"
                disabled={isAnalyzing}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-600 transition-colors bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 ${
                  formErrors.brandName ? 'border-red-300 dark:border-red-700 focus:ring-red-500' : 'border-gray-200 dark:border-slate-700'
                } ${isAnalyzing ? 'bg-gray-50 dark:bg-slate-800 cursor-not-allowed' : ''}`}
              />
              {formErrors.brandName && (
                <p className="mt-1 text-red-600 text-sm flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {formErrors.brandName}
                </p>
              )}
            </div>

            <div className="relative pb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                Competitor Website URL *
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.spyUrl}
                  onChange={(e) => handleInputChange('spyUrl', e.target.value)}
                  placeholder="e.g., betwin-casino.com"
                  disabled={isAnalyzing}
                  className={`w-full px-4 py-3 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-600 transition-colors bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 ${
                    formErrors.spyUrl ? 'border-red-300 dark:border-red-700 focus:ring-red-500' : urlVerified ? 'border-green-400 dark:border-green-600' : 'border-gray-200 dark:border-slate-700'
                  } ${isAnalyzing ? 'bg-gray-50 dark:bg-slate-800 cursor-not-allowed' : ''}`}
                />
                {verifyingUrl && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                  </div>
                )}
                {!verifyingUrl && urlVerified && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  </div>
                )}
              </div>
              {formErrors.spyUrl && (
                <p className="absolute left-0 bottom-0 text-red-600 dark:text-red-400 text-sm flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {formErrors.spyUrl}
                </p>
              )}
              {verificationMessage && !formErrors.spyUrl && (
                <p className={`absolute left-0 bottom-0 text-sm flex items-center gap-1 ${urlVerified ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {urlVerified ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                  {verificationMessage}
                </p>
              )}
            </div>
          </div>

          <div className="mt-4">
            <button
              onClick={handleStartAnalysis}
              disabled={isAnalyzing}
              className={`font-medium py-3 px-6 rounded-lg transition-all duration-300 flex items-center gap-2 ${
                isAnalyzing
                  ? 'bg-gray-400 cursor-not-allowed text-white'
                  : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
              }`}
            >
              {isAnalyzing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Starting Analysis...</span>
                </>
              ) : (
                <>
                  <Search className="w-4 h-4" />
                  <span>Start Analysis</span>
                </>
              )}
            </button>
            {isAnalyzing && (
              <p className="text-sm text-gray-600 dark:text-slate-400 mt-2">
                This may take a few moments. The brand will appear below once processing begins.
              </p>
            )}
          </div>

          {/* Active Processing Status */}
          {hasActiveProcessing && (
            <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                <span className="text-blue-800 dark:text-blue-200 font-medium">Processing in progress</span>
              </div>
              {Object.entries(processingBrands).map(([brandUrl, info]) => (
                info.isActive && (
                  <div key={brandUrl} className="ml-6 text-sm text-blue-700 dark:text-blue-300">
                    <span className="font-medium">{info.brandName}</span>: {info.step}
                    {info.progress > 0 && (
                      <div className="mt-1 w-full bg-blue-200 dark:bg-blue-800 rounded-full h-1.5">
                        <div
                          className="bg-blue-600 h-1.5 rounded-full transition-all duration-500"
                          style={{ width: `${info.progress}%` }}
                        />
                      </div>
                    )}
                  </div>
                )
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Brand Cards Section */}
      <div className="space-y-6">
        {isLoadingBrands ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600 dark:text-blue-400" />
            <span className="ml-2 text-gray-600 dark:text-slate-400">Loading brands...</span>
          </div>
        ) : brands.length > 0 ? (
          <>
            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-800 dark:text-slate-100">
                Your Competitor Brands ({brands.length})
              </h3>
            </div>
            {brands.map((brand) => {
              const processingInfoForBrand = processingBrands[brand.brand_url];
              if (brand.processing_status && brand.processing_status !== 'completed') {
              }
              return (
                <CompCard
                  key={brand.id || brand.brand_url}
                  brand={brand}
                  onStatusUpdate={handleBrandStatusUpdate}
                  processingInfo={processingInfoForBrand}
                />
              );
            })}
          </>
        ) : (
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-8 text-center">
            <Eye className="w-12 h-12 text-gray-400 dark:text-slate-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-800 dark:text-slate-100 mb-2">No Competitor Brands Yet</h3>
            <p className="text-gray-600 dark:text-slate-400">
              Start by adding your first competitor.
            </p>
          </div>
        )}
      </div>

      {/* Processing Popup with 30-second countdown */}
      <ProcessingPopup
        isOpen={showProcessingPopup}
        onClose={handleCloseProcessingPopup}
        brandName={processingBrandInfo.brandName}
        brandUrl={processingBrandInfo.brandUrl}
        processingStatus={processingBrands[processingBrandInfo.brandUrl]?.status || '1'}
        isCompleted={processingBrands[processingBrandInfo.brandUrl]?.status === 'completed'}
        hasError={['error', 'failed'].includes(processingBrands[processingBrandInfo.brandUrl]?.status)}
        liveAffiliatesCount={processingBrands[processingBrandInfo.brandUrl]?.liveAffiliatesCount || 0}
      />
    </div>
  );
};

export default CompetitorSpy;